/**
 * BuilderPagePreview
 * Renders a single proposal page as a clean white printed document — no builder
 * chrome. Renders the exact same DnD sections/elements as the canvas so that
 * canvas and preview stay in sync automatically.
 *
 * All page types (cover, cover_letter, line_items, signature, custom…) render
 * their sections[].elements[] here. There is no separate static layout per page
 * type — the canvas IS the source of truth.
 */
import { cn } from "@/lib/utils";
import type {
  ProposalDocumentLayoutPage,
  TemplatePageLayoutMode,
} from "../../types";
import { BuilderElementPreview, type BrandPreviewData } from "./BuilderElementPreview";
import type { BuilderElementDraft } from "./BuilderSectionElementList";
import { useBrandSettings, useBrandAssets, useBrandFonts } from "../../hooks/useBrandSettings";
import { resolveBrandTypography, buildFontFaceCSS } from "../../utils/brandTypography";
import type { ProposalCanvasContext } from "./ProposalBuilderCanvas";

interface PageSection {
  id:         string;
  layoutMode: TemplatePageLayoutMode;
  elements:   BuilderElementDraft[];
  settings?:  Record<string, string | boolean | number | null | undefined>;
}

export interface BuilderPagePreviewProps {
  page:             ProposalDocumentLayoutPage;
  className?:       string;
  proposalContext?: ProposalCanvasContext;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalizeSections(page: ProposalDocumentLayoutPage): PageSection[] {
  if (!Array.isArray(page.sections)) return [];

  return page.sections.map((section, index) => {
    const rec =
      section && typeof section === "object" && !Array.isArray(section)
        ? (section as Record<string, unknown>)
        : {};

    const rawLayout = rec.layoutMode;
    const layoutMode: TemplatePageLayoutMode =
      rawLayout === "stack"        || rawLayout === "two_column"  ||
      rawLayout === "three_column" || rawLayout === "split_left"  ||
      rawLayout === "split_right"  || rawLayout === "grid"        ||
      rawLayout === "custom"
        ? rawLayout : "stack";

    return {
      id:       typeof rec.id === "string" && rec.id.length > 0 ? rec.id : `s_${index}`,
      layoutMode,
      elements: Array.isArray(rec.elements) ? (rec.elements as BuilderElementDraft[]) : [],
      settings: rec.settings && typeof rec.settings === "object" && !Array.isArray(rec.settings)
        ? (rec.settings as Record<string, string | boolean | number | null | undefined>)
        : undefined,
    };
  });
}

function hexToRgba(hex: string, opacity: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return hex;
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, opacity))})`;
}

function getGridClass(layoutMode: TemplatePageLayoutMode) {
  switch (layoutMode) {
    case "two_column":   return "grid-cols-1 md:grid-cols-2";
    case "three_column": return "grid-cols-1 md:grid-cols-3";
    case "split_left":   return "grid-cols-1 md:grid-cols-[1.35fr_0.65fr]";
    case "split_right":  return "grid-cols-1 md:grid-cols-[0.65fr_1.35fr]";
    case "grid":         return "grid-cols-1 md:grid-cols-2";
    default:             return "grid-cols-1";
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BuilderPagePreview({ page, className, proposalContext }: BuilderPagePreviewProps) {
  const { data: brandSettings }    = useBrandSettings();
  const { data: brandAssets = [] } = useBrandAssets();
  const { data: brandFonts = [] }  = useBrandFonts();

  const useCustomColors = brandSettings?.use_custom_colors ?? false;
  const rawPrimary      = brandSettings?.color_palette?.primary;
  const primaryColor    = useCustomColors && typeof rawPrimary === "string" ? rawPrimary : null;
  const textColor       = useCustomColors ? (brandSettings?.text_color ?? null) : null;
  const bgColor         = useCustomColors ? (brandSettings?.background_color ?? null) : null;

  const typography     = resolveBrandTypography(brandSettings, brandFonts);
  const fontFaceCSS    = buildFontFaceCSS(typography);

  const secondaryLogoAsset = brandAssets.find((a) => a.asset_role === "secondary_logo");
  const addressParts       = [
    brandSettings?.address_line1,
    brandSettings?.address_line2,
    brandSettings?.city,
    brandSettings?.state,
    brandSettings?.zip,
  ].filter(Boolean);

  const brand: BrandPreviewData = {
    companyName:      brandSettings?.company_name      ?? null,
    primaryLogoUrl:   brandSettings?.logo_url          ?? null,
    secondaryLogoUrl: secondaryLogoAsset?.public_url   ?? null,
    phone:            brandSettings?.phone             ?? null,
    website:          brandSettings?.website           ?? null,
    email:            brandSettings?.email             ?? null,
    address:          addressParts.length > 0 ? addressParts.join(", ") : null,
    tagline:          brandSettings?.tagline           ?? null,
    primaryColor,
    textColor,
    backgroundColor:  bgColor,
    headingFontFamily: typography.headingFont.fontFamily,
    bodyFontFamily:    typography.bodyFont.fontFamily,
    coverLetterHtml:  brandSettings?.cover_letter_html ?? null,
    signoff:          brandSettings?.signoff           ?? null,
    // Proposal-specific context (threaded from editor)
    prospectName:    proposalContext?.prospectName     ?? null,
    prospectAddress: proposalContext?.prospectAddress  ?? null,
    lineItems:       proposalContext?.lineItems,
    subtotal:        proposalContext?.subtotal         ?? null,
    total:           proposalContext?.total            ?? null,
  };

  const sections = normalizeSections(page);
  const hasSections = sections.some((s) => s.elements.length > 0);

  return (
    <>
      {fontFaceCSS && <style>{fontFaceCSS}</style>}
      <div
        className={cn(
          "relative w-full overflow-hidden",
          className,
        )}
        style={{
          aspectRatio: "8.5 / 11",
          backgroundColor: bgColor ?? "#ffffff",
          fontFamily: typography.bodyFont.fontFamily,
        }}
      >
        {/* ── Page-type settings header (rendered above sections) ── */}
        {(() => {
          const ps = page.settings as Record<string, unknown> | null | undefined;
          if (!ps) return null;
          if (page.type === "cover") {
            const headline    = typeof ps.headline    === "string" ? ps.headline    : "";
            const subheadline = typeof ps.subheadline === "string" ? ps.subheadline : "";
            if (!headline && !subheadline) return null;
            return (
              <div className="px-10 pt-10 space-y-1.5">
                {headline    && <p className="text-2xl font-bold text-gray-800">{headline}</p>}
                {subheadline && <p className="text-base text-gray-500">{subheadline}</p>}
              </div>
            );
          }
          if (page.type === "cover_letter" || page.type === "line_items" || page.type === "custom") {
            const showTitle = page.type !== "custom" || ps.showPageTitle !== false;
            const title     = typeof ps.title     === "string" ? ps.title     : "";
            const introText = typeof ps.introText === "string" ? ps.introText : "";
            if ((!showTitle || !title) && !introText) return null;
            return (
              <div className="px-10 pt-10 space-y-1.5">
                {showTitle && title && <p className="text-2xl font-bold text-gray-800">{title}</p>}
                {introText && <p className="text-base leading-relaxed text-gray-500">{introText}</p>}
              </div>
            );
          }
          return null;
        })()}

        {hasSections ? (
          <div className="px-10 py-10 space-y-10">
            {sections.map((section, index) => {
              if (section.elements.length === 0) return null;
              const rawBg   = section.settings?.backgroundColor as string | undefined;
              const sectionBg = typeof rawBg === "string" && rawBg ? rawBg : null;
              const bgOpacity = typeof section.settings?.backgroundOpacity === "number"
                ? section.settings.backgroundOpacity / 100 : 1;
              const bgStyle = sectionBg
                ? { backgroundColor: hexToRgba(sectionBg, bgOpacity) }
                : undefined;
              return (
                <div key={section.id} style={bgStyle} className={cn(sectionBg ? "rounded-2xl px-4 py-4" : undefined)}>
                  {index > 0 && !sectionBg && <div className="mb-10 border-t border-black/5" />}
                  <div className={cn("grid gap-8", getGridClass(section.layoutMode))}>
                    {section.elements.map((element) => (
                      <BuilderElementPreview
                        key={element.id}
                        element={element}
                        brand={brand}
                        pageSettings={page.settings as Record<string, unknown> | undefined}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-3 text-center px-8">
            <p className="text-sm font-medium text-gray-400">No content yet</p>
            <p className="text-xs text-gray-300">
              Add sections and elements in the Canvas tab to see them here.
            </p>
          </div>
        )}

        {/* ── Cover letter closing block (settings-driven) ── */}
        {page.type === "cover_letter" && (() => {
          const ps = page.settings as Record<string, unknown> | null | undefined;
          const showSignature = ps?.showCompanySignature !== false;
          const showDate      = ps?.showDate             !== false;
          const closingText   = typeof ps?.closingText === "string" ? ps.closingText : "";
          if (!closingText && !showSignature && !showDate) return null;
          return (
            <div className="px-10 pb-10 space-y-1.5 border-t border-black/8 pt-5">
              {closingText && <p className="text-sm leading-relaxed text-gray-500">{closingText}</p>}
              {showSignature && brand.companyName && (
                <p className="text-sm font-medium text-gray-700">{brand.companyName}</p>
              )}
              {showDate && (
                <p className="text-xs text-gray-400">
                  {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>
          );
        })()}

        {/* ── Closing page structured blocks (settings-driven) ── */}
        {page.type === "closing" && (() => {
          const ps = page.settings as Record<string, unknown> | null | undefined;
          const showSummary  = ps?.showSummaryBlock  !== false;
          const showCta      = ps?.showCallToAction  !== false;
          const showContact  = ps?.showContactBlock  !== false;
          if (!showSummary && !showCta && !showContact) return null;
          const summaryHeading = typeof ps?.summaryHeading === "string" ? ps.summaryHeading : "Next Steps";
          const closingMessage = typeof ps?.closingMessage  === "string" ? ps.closingMessage : "";
          const ctaLabel       = typeof ps?.ctaLabel        === "string" ? ps.ctaLabel       : "Ready to get started";
          const ctaText        = typeof ps?.ctaText         === "string" ? ps.ctaText        : "";
          return (
            <div className="px-10 pb-10 space-y-4 border-t border-black/8 pt-6">
              {showSummary && (
                <div className="rounded-xl border border-black/10 bg-black/[0.02] px-5 py-4">
                  <p className="text-sm font-semibold text-gray-700">{summaryHeading}</p>
                  {closingMessage && <p className="mt-2 text-sm leading-relaxed text-gray-500">{closingMessage}</p>}
                </div>
              )}
              {showCta && (
                <div className="rounded-xl px-5 py-4 text-white" style={{ backgroundColor: primaryColor ?? "#1a1a2e" }}>
                  <p className="font-semibold">{ctaLabel}</p>
                  {ctaText && <p className="mt-1 text-sm text-white/70">{ctaText}</p>}
                </div>
              )}
              {showContact && (brand.companyName || brand.phone || brand.email || brand.website) && (
                <div className="space-y-1 text-sm text-gray-500">
                  {brand.companyName && <p className="font-medium text-gray-700">{brand.companyName}</p>}
                  {brand.phone   && <p>{brand.phone}</p>}
                  {brand.email   && <p>{brand.email}</p>}
                  {brand.website && <p>{brand.website}</p>}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </>
  );
}
